// Minimal ESC/POS encoder for 58mm / 80mm thermal printers.
//
// Produces a Uint8Array of printer command bytes from a Pointbunny sale object -
// the same data the HTML receipt (receiptView.js) renders, but as ESC/POS so it
// can be streamed to a Bluetooth thermal printer (btPrinter.js).
//
// Text is reduced to printable ASCII: generic thermal printers default to the
// CP437 code page and won't render UTF-8, so non-ASCII characters are
// transliterated where possible and otherwise dropped (best-effort, never throws).

import { orderTypeLabel } from "./orderTypeLabel.js";
import { formatInvoiceNumber, invoiceLabel } from "./invoiceNumber.js";
import { formatMoney } from "./currency.js";
import { extractInclusiveTax, taxLabel, splitTaxableGross, exemptLabel } from "./inclusiveTax.js";
import { redeemedRewardLineIds } from "./loyalty.js";
import { qrMatrix } from "./receiptQr.js";

const ESC = 0x1b;
const GS = 0x1d;

// Characters per line at Font A: 58mm paper fits ~32, 80mm fits ~48.
const COLS = { 58: 32, 80: 48 };

class Encoder {
  constructor(cols) {
    this.cols = cols;
    this.bytes = [];
  }

  raw(...b) {
    for (const x of b) this.bytes.push(x & 0xff);
    return this;
  }

  _ascii(str) {
    return String(str ?? "")
      .replace(/[‘’′]/g, "'")
      .replace(/[“”″]/g, '"')
      .replace(/[–—]/g, "-")
      .replace(/₱/g, "P") // peso sign -> P (printer-safe)
      .replace(/[^\x20-\x7E]/g, ""); // drop remaining non-ASCII
  }

  text(str) {
    const s = this._ascii(str);
    for (let i = 0; i < s.length; i++) this.bytes.push(s.charCodeAt(i) & 0xff);
    return this;
  }

  lf(n = 1) {
    for (let i = 0; i < n; i++) this.bytes.push(0x0a);
    return this;
  }

  line(str = "") {
    return this.text(str).lf();
  }

  init() {
    return this.raw(ESC, 0x40); // ESC @  (reset)
  }

  align(n) {
    return this.raw(ESC, 0x61, n); // 0 left, 1 center, 2 right
  }

  bold(on) {
    return this.raw(ESC, 0x45, on ? 1 : 0);
  }

  // QR code as a GS v 0 raster image (supported far more widely than the
  // native GS ( k QR command). One module = `scale` dots, sized to ~80% of
  // the paper (Font A is 12 dots per column), with a 4-module quiet zone.
  qr(matrix) {
    const q = 4;
    const n = matrix.size + q * 2;
    const scale = Math.max(2, Math.min(6, Math.floor((this.cols * 12 * 0.8) / n)));
    const wDots = n * scale;
    const wBytes = Math.ceil(wDots / 8);
    this.raw(GS, 0x76, 0x30, 0, wBytes & 0xff, wBytes >> 8, wDots & 0xff, wDots >> 8);
    for (let y = 0; y < wDots; y++) {
      const r = Math.floor(y / scale) - q;
      for (let xb = 0; xb < wBytes; xb++) {
        let byte = 0;
        for (let bit = 0; bit < 8; bit++) {
          const x = xb * 8 + bit;
          const c = Math.floor(x / scale) - q;
          if (x < wDots && r >= 0 && r < matrix.size && c >= 0 && c < matrix.size && matrix.dark(r, c)) {
            byte |= 0x80 >> bit;
          }
        }
        this.bytes.push(byte);
      }
    }
    return this.lf();
  }

  // width / height are 1..8 multipliers.
  size(w, h) {
    const n = (((w - 1) & 0x07) << 4) | ((h - 1) & 0x07);
    return this.raw(GS, 0x21, n); // GS !
  }

  divider(ch = "-") {
    return this.line(ch.repeat(this.cols));
  }

  // A left label and a right value padded to fill the line. If the label is too
  // long it wraps onto following lines with the value kept on the first line.
  row(left, right) {
    left = this._ascii(left);
    right = this._ascii(right);
    const max = this.cols - right.length - 1;
    if (left.length <= max || max <= 0) {
      const pad = Math.max(1, this.cols - left.length - right.length);
      return this.line(left + " ".repeat(pad) + right);
    }
    const first = left.slice(0, max);
    const pad = Math.max(1, this.cols - first.length - right.length);
    this.line(first + " ".repeat(pad) + right);
    let rest = left.slice(max);
    while (rest.length) {
      this.line(rest.slice(0, this.cols));
      rest = rest.slice(this.cols);
    }
    return this;
  }

  cut() {
    return this.lf(4).raw(GS, 0x56, 0x42, 0x00); // feed + partial cut (ignored if no cutter)
  }

  done() {
    return new Uint8Array(this.bytes);
  }
}

// Thermal printers can't render symbols like ₱/€/£, so print the currency CODE
// instead (e.g. "PHP 100.00"). Currency follows the business country.
const money = (n) => formatMoney(n, { ascii: true });
// Negative amounts read better as "-PHP 5.00" than "PHP -5.00".
const signedMoney = (n) => (n < 0 ? "-" + money(Math.abs(n)) : "+" + money(n));

export function encodeReceipt(sale, { width = 80 } = {}) {
  const cols = COLS[width] ?? COLS[80];
  const e = new Encoder(cols);

  const date = new Date(sale.date);
  const dateStr = date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const storeName = sale.storeName?.includes("@")
    ? "POINTBUNNY POS"
    : (sale.storeName ?? "POINTBUNNY POS");
  const paymentMethod = sale.paymentMethod ?? "cash";
  const paymentLabel =
    { cash: "Cash", card: "Card", qr: "QR" }[paymentMethod] ?? "Cash";

  e.init();

  // ── Header ──
  e.align(1).bold(true).size(2, 2).line(storeName).size(1, 1).bold(false);

  if (sale.addressStreet) {
    e.line(sale.addressStreet);
    const locality = [sale.addressCity, sale.addressProvince]
      .filter(Boolean)
      .join(", ");
    const localityZip = [locality, sale.addressZip].filter(Boolean).join(" ");
    if (localityZip) e.line(localityZip);
  }
  if (sale.businessPhone && sale.showBusinessPhone !== false) e.line(sale.businessPhone);

  e.line(`${dateStr}  ${timeStr}`);

  if (sale.showInvoiceNumber !== false && sale.receiptNumber != null) {
    e.bold(true)
      .line(
        `${invoiceLabel(sale.addressCountry)} ${formatInvoiceNumber(sale.receiptNumber, sale.date)}`,
      )
      .bold(false);
  }
  if (sale.cashierName && sale.showCashier !== false) {
    e.line(`Cashier: ${sale.cashierName}`);
  }
  if (sale.orderType) {
    e.bold(true)
      .line(orderTypeLabel(sale.orderType, sale.addressCountry, { upper: true }))
      .bold(false);
  }
  if (sale.tableName) {
    e.bold(true).line(`TABLE ${sale.tableName}`).bold(false);
  }
  if (sale.showTicketNumber !== false && sale.ticketNumber != null) {
    e.lf().bold(true).size(2, 2).line(`TICKET #${sale.ticketNumber}`).size(1, 1).bold(false);
  }

  // ── Items ──
  // Free-item reward redemptions print as $0 (FREE) on the item's own row
  // instead of a separate "Free X (Reward)" discount line - no strikethrough
  // on plain ESC/POS text, so a (FREE) tag carries the same meaning.
  const redeemedLineIds = redeemedRewardLineIds(sale.adjustments);
  e.align(0).divider();
  for (const item of sale.items) {
    const isFreeReward = redeemedLineIds.has(item.lineId);
    const label = `${item.itemName} x${item.quantity}${isFreeReward ? " (FREE)" : ""}`;
    e.row(label, money(isFreeReward ? 0 : item.totalPrice));
    const variants = item.selectedVariants
      ?.map((v) => v.variantName)
      .filter(Boolean)
      .join(", ");
    if (variants) e.line("  " + variants);
  }

  // ── Adjustments ──
  const activeAdj = (sale.adjustments ?? []).filter(
    (a) => !a.removed && a.computedAmount !== undefined && a.source !== "loyalty_reward",
  );
  const removedAdj = sale.showRemovedAdjustments
    ? (sale.removedAdjustments ?? [])
    : [];
  if (activeAdj.length || removedAdj.length) {
    e.divider();
    e.row("Subtotal", money(sale.subtotal ?? sale.totalPrice));
    for (const adj of activeAdj) {
      const name =
        adj.name +
        (adj.calculation === "percentage" ? ` (${adj.appliedValue}%)` : "");
      e.row(name, signedMoney(Number(adj.computedAmount)));
    }
    for (const adj of removedAdj) {
      const name =
        adj.name +
        (adj.calculation === "percentage" ? ` (${adj.appliedValue}%)` : "") +
        " (removed)";
      e.row(name, "--");
    }
  }

  // ── Totals ──
  e.divider();
  e.bold(true).size(1, 2).row("TOTAL", money(sale.totalPrice)).size(1, 1).bold(false);
  // Tax-inclusive breakdown (total unchanged; tax is extracted from it).
  // Per-item exemption (2026-07-07): only taxable items feed the VAT extraction;
  // exempt items get their own gross line. Gated on the sale actually being
  // tax-inclusive so an all-exempt sale still prints its exempt-sales line.
  const isInclusiveSale = Number(sale.taxInclusiveRate) > 0;
  const { taxableGross, exemptGross } = splitTaxableGross(sale.items, (it) => it.taxClass === "exempt");
  const vat = isInclusiveSale ? extractInclusiveTax(taxableGross, sale.taxInclusiveRate) : null;
  if (vat) {
    const lbl = taxLabel(sale.addressCountry);
    e.row(`${lbl}able Sales`, money(vat.net));
    e.row(`${lbl} (${vat.rate}%)`, money(vat.tax));
  }
  // Tax added at checkout: a sale with a tax line also lists its exempt sales.
  const hasAddedTax = (sale.adjustments ?? []).some((a) => a.isTax && !a.removed);
  if ((isInclusiveSale || hasAddedTax) && exemptGross > 0) {
    e.row(`${exemptLabel(sale.addressCountry)} Sales`, money(exemptGross));
  }
  // Tip sits outside the sale total (never taxed).
  const tip = Number(sale.tipAmount) || 0;
  if (tip > 0) {
    e.row("Tip", money(tip));
    e.bold(true).row("TOTAL WITH TIP", money(Number(sale.totalPrice) + tip)).bold(false);
  }
  e.row("Payment", paymentLabel);
  if (paymentMethod === "cash") {
    e.row("Cash Tendered", money(sale.customerPayment));
    e.bold(true).row("Change", money(sale.customerChange)).bold(false);
  } else {
    e.bold(true).row("Paid in full", money(Number(sale.totalPrice) + tip)).bold(false);
  }

  // ── Footer ──
  e.divider();
  // Custom footer + feedback QR are Pro-only (free keeps the default line).
  const paid = !sale.isFreePlan;
  e.align(1).line(paid && sale.receiptFooter ? sale.receiptFooter : "Thank you for your purchase!");
  const feedbackQr = paid ? qrMatrix(sale.receiptQrUrl) : null;
  if (feedbackQr) e.qr(feedbackQr);
  // Free-plan branding (see CLAUDE.md "Free vs. Paid Plan Gating") - a slogan
  // pitched at the customer holding the receipt, not an "upgrade" nag. It takes
  // the place of the "Powered by" line as one short spaced-out block with no
  // extra divider rules (the HTML receipt draws it as a single rounded badge).
  // The slogan is split in two so it never wraps mid-word on 58mm (32 cols).
  if (sale.isFreePlan) {
    e.lf()
      .bold(true).line("P O I N T B U N N Y   P O S").bold(false)
      .line("The all-in-one POS")
      .line("to run your shop")
      .bold(true).line("pointbunny.com").bold(false);
  } else {
    e.bold(true).line("Powered by Pointbunny POS").bold(false);
  }
  e.cut();

  return e.done();
}
